import React from "react";
import Logo from '../../../../assets/images/logo_radius_trend.png'; // Sesuaikan path
import { BaseUrl } from "../../../../utilities/BaseUrl";

// Helper format harga
const formatPrice = (priceStr) => {
  if (!priceStr) return "Rp 0";
  return `Rp ${Number(priceStr).toLocaleString('id-ID')}`;
};

/* =============================================================
   VOUCHER CARD (MAPPING DATA API)
============================================================= */
const VoucherCard = ({ voucher }) => {
  // Mapping data dari response API ke kebutuhan template
  const code = voucher.username || "-";
  const price = formatPrice(voucher.profile?.price);
  const uptime = voucher.profile?.session_timeout_formatted || "-";
  const active = voucher.profile?.validity_formatted || "-";
  const quota = voucher.profile?.total_limit_bytes_formatted || "Unlimited";
  const description = voucher.company?.name || "Voucher Internet";

  return (
    <div className="voucher-card">
      {/* HEADER */}
      <div className="voucher-header">
        <div style={{ display: "flex", alignItems: "center", gap: "1mm", minWidth: 0 }}>
          {voucher?.company?.logo && <img src={BaseUrl + "/storage/" + voucher?.company?.logo} alt="Logo" className="voucher-logo" />}
          <div style={{ minWidth: 0 }}>
            <p className="voucher-brand">{description}</p>
            <p className="voucher-subtitle">WiFi Voucher</p>
          </div>
        </div>
        <div className="voucher-price">{price}</div>
      </div>

      {/* BODY */}
      <div className="voucher-body">
        {/* LEFT */}
        <div className="voucher-left">
          <p className="voucher-label">KODE VOUCHER</p>
          <p className="voucher-code">{code}</p>

          {/* INFO */}
          <div className="voucher-info">
            <div className="voucher-info-row">
              <span>Up Time</span>
              <strong>{uptime}</strong>
            </div>
            <div className="voucher-info-row">
              <span>Masa Aktif</span>
              <strong>{active}</strong>
            </div>
            <div className="voucher-info-row">
              <span>Kuota</span>
              <strong>{quota}</strong>
            </div>
          </div>
        </div>

        {/* RIGHT (QR PLACEHOLDER) */}
        <div className="voucher-right">
          <div className="voucher-qr">
            <div className="qr-pattern">QR</div>
          </div>
          <p className="voucher-scan">Scan untuk login</p>
        </div>
      </div>

      {/* FOOTER */}
      <div className="voucher-footer">
        <span>{description}</span>
        <span>Jangan buang kode voucher</span>
      </div>
    </div>
  );
};

/* =============================================================
   MAIN PRINTABLE LAYOUT (A4 PAPER & GRID)
============================================================= */
export const PrintableVoucherGrid = ({ vouchers, columns }) => {
  // Dynamic grid columns based on config
  const gridStyle = `
    .voucher-grid {
      grid-template-columns: repeat(${columns}, minmax(0, 1fr)) !important;
    }
  `;

  return (
    <>
      <div className="a4-paper">
        <div className="voucher-grid">
          {vouchers.map((voucher, index) => (
            <VoucherCard key={voucher.id || index} voucher={voucher} />
          ))}
        </div>
      </div>

      {/* ALL STYLES INJECTED HERE */}
      <style>{`
        /* A4 PAPER */
        .a4-paper {
          width: 210mm; min-height: 297mm; margin: 0 auto; padding: 6mm;
          box-sizing: border-box; background: white;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
        }

        /* GRID */
        .voucher-grid {
          display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
          grid-auto-rows: max-content; align-content: start;
          column-gap: 2.5mm; row-gap: 3mm;
        }

        /* VOUCHER CARD */
        .voucher-card {
          width: 100%; aspect-ratio: 1.62 / 1; min-width: 0; overflow: hidden;
          position: relative; box-sizing: border-box; display: flex; flex-direction: column;
          background: white; border: 0.35mm solid #00b4d8; border-radius: 3mm;
          color: #0f172a; box-shadow: 0 1mm 2mm rgba(15, 23, 42, 0.10);
          font-family: Inter, Arial, sans-serif;
        }

        /* HEADER */
        .voucher-header {
          display: flex; align-items: center; justify-content: space-between; gap: 1mm;
          padding: 1mm; background: linear-gradient(135deg, #f0fdff 0%, #ffffff 100%);
          border-bottom: 0.5mm solid #dff7fc;
        }
        .voucher-logo { width: 6mm; height: 6mm; object-fit: contain; flex-shrink: 0; }
        .voucher-brand { margin: 0; font-size: 7px; line-height: 1.1; font-weight: 800; color: #0077b6; white-space: nowrap; }
        .voucher-subtitle { margin: 0; font-size: 4.5px; line-height: 1.1; color: #475569; font-weight: 600; white-space: nowrap; }
        .voucher-price { padding: 1mm 1.2mm; border-radius: 1.5mm; background: #0077b6; color: white; font-size: 6px; line-height: 1; font-weight: 800; white-space: nowrap; }

        /* BODY */
        .voucher-body {
          flex: 1; min-height: 0; display: grid;
          grid-template-columns: minmax(0, 1fr) 15mm; gap: 1.5mm; padding: 1.5mm 2mm 1mm;
        }
        .voucher-left { min-width: 0; display: flex; flex-direction: column; justify-content: center; }
        .voucher-label { margin: 0; font-size: 4.5px; line-height: 1.1; color: #334155; font-weight: 700; letter-spacing: 0.1px; }
        .voucher-code { margin: 0 1mm 0 0 ; font-size: 10px; line-height: 1; color: #006da6; font-weight: 900; letter-spacing: 0.25px;  }

        /* INFO */
        .voucher-info { margin-top: 1mm; padding: 1.2mm 1.5mm; border: 0.25mm solid #8ddbea; border-radius: 1.5mm; background: #f4fcfe; }
        .voucher-info-row { display: flex; align-items: center; justify-content: space-between; gap: 1mm; font-size: 4px; line-height: 1.55; color: #334155; font-weight: 500; }
        .voucher-info-row strong { color: #006da6; font-size: 5px; font-weight: 800; white-space: nowrap; }

        /* RIGHT & QR */
        .voucher-right { min-width: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
        .voucher-qr { width: 10mm; height: 10mm; padding: 1mm; box-sizing: border-box; border: 0.3mm solid #0077b6; border-radius: 1.5mm; background: white; display: flex; align-items: center; justify-content: center; }
        .qr-pattern { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; border-radius: 0.5mm; background: repeating-linear-gradient(45deg, #0f172a 0px, #0f172a 1px, white 1px, white 2px); color: white; font-size: 3px; font-weight: 900; }
        .voucher-scan { margin: 0.8mm 0 0; font-size: 4px; line-height: 1.1; color: #334155; font-weight: 600; text-align: center; white-space: nowrap; }

        /* FOOTER */
        .voucher-footer { display: flex; align-items: center; justify-content: space-between; gap: 1mm; padding: 1.2mm 2mm; background: #00b4d8; color: white; font-size: 5px; line-height: 1.1; font-weight: 800; white-space: nowrap; }

        /* PRINT MODE */
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body { width: 210mm; margin: 0; padding: 0; background: white !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .a4-paper { width: 210mm; height: 297mm; min-height: 297mm; margin: 0; padding: 6mm; box-shadow: none; overflow: hidden; }
          .voucher-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none; }
          
          /* TAMBAHAN: Sembunyikan tombol saat print */
          .no-print { display: none !important; } 
        }
      `}</style>
      
      {/* Dynamic Grid Override */}
      <style>{gridStyle}</style>
    </>
  );
};